import { Capacitor } from '@capacitor/core';
import { Store } from '../redux/store';

/**
 * Thrown when APK validation returns false.
 */
export class AppBlockedError extends Error {
  constructor() {
    super('API call blocked: APK validation failed.');
    this.name = 'AppBlockedError';
  }
}

/**
 * Thrown when the device is detected as rooted (confidence >= 3).
 */
export class RootedDeviceError extends Error {
  constructor() {
    super('API call blocked: rooted device detected.');
    this.name = 'RootedDeviceError';
  }
}

/**
 * Polls Redux state until the value resolves from null with a 10s timeout.
 */
function waitForState<T>(
  selector: () => T | null,
  timeoutMs = 10000
): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('Security check timed out')), timeoutMs);

    const check = () => {
      const value = selector();
      if (value !== null) {
        clearTimeout(timeout);
        resolve(value as T);
        return;
      }
      setTimeout(check, 100);
    };

    check();
  });
}

/**
 * Drop-in replacement for fetch() with APK validation + root detection interceptor.
 *
 * Before every request:
 *   APK valid = false    → blocked, AppBlockedError thrown
 *   Device rooted (high risk) → blocked, RootedDeviceError thrown
 *   Both pass            → request proceeds normally
 */
export async function authFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  if (!Capacitor.isNativePlatform()) {
    return fetch(input, init);
  }

  const valid = await waitForState<boolean>(() => Store.getState().appToken.valid);

  if (!valid) {
    throw new AppBlockedError();
  }

  const isHighRisk = await waitForState<boolean>(() => Store.getState().rootDetection.isHighRisk);

  if (isHighRisk) {
    throw new RootedDeviceError();
  }

  return fetch(input, init);
}
